import React, {useState, useEffect, useRef, useLayoutEffect} from 'react';
import { useParams, useLocation  } from 'react-router-dom';
import { Button, Card, CardHeader, CardText, Carousel, CarouselItem, Collapse, ListGroup, ListGroupItem } from 'react-bootstrap';
import { Play, Pause, Volume2, VolumeX, ChevronLeft, RotateCcw, RotateCw, Maximize, Minimize  } from "lucide-react";
import ReactPlayer from 'react-player';
import HeroBackground from '../HeroBackground';
import Spinner from "../Spinner";

function MovieProfile({ setShowVideoPlayer }) {
  const [error, setError] = useState(null);
  const { moviename: movieParam } = useParams();
  const [isLoaded, setIsLoaded] = useState(false);
  const [movieData, setMovieData] = useState(null);
 
  const [savedTime, setSavedTime] = useState(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const wrapperRef = useRef(null);
  const playerRef = useRef(null);
  const [played, setPlayed] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [hasStarted, setHasStarted] = useState(false);
  const lastPlayedTimeRef = useRef(0);
  const [showSlider, setShowSlider] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = React.useState(false);
  const [muted, setMuted] = useState(false);
  const sliderRef = useRef(null);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverX, setHoverX] = useState(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [showControls, setShowControls] = useState(true);
  const inactivityTimeoutRef = useRef(null);
const movieDataRef = useRef(null);
const lastSaveRef = useRef(0);
const hasResumedRef = useRef(false);
const currentTimeRef = useRef(0);

useEffect(() => {
  movieDataRef.current = movieData;
}, [movieData]);
  

  




  const resetInactivityTimer = () => {
    setShowControls(true); // show controls on any interaction
  
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
  
    inactivityTimeoutRef.current = setTimeout(() => {
      setShowControls(false); // hide controls after 3 seconds
    }, 3000); // adjust this timeout as needed
  };

  useEffect(() => {
    const handleUserActivity = () => resetInactivityTimer();
  
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);
  
    resetInactivityTimer(); // Start timer on mount
  
    return () => {
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, []);
  
  

  const [duration, setDuration] = React.useState(0);
  const handleDuration = (dur) => {
    setDuration(dur);
  };
  const hideTimeoutRef = useRef(null);

  function formatTime(seconds) {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  }
  
  const currentTime = played * duration;
  const totalTime = duration;
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);


  const handleRewind15 = () => {
    const currentTime = playerRef.current.getCurrentTime();
    playerRef.current.seekTo(Math.max(currentTime - 15, 0), 'seconds');
  };
  
  const handleForward15 = () => {
    const currentTime = playerRef.current.getCurrentTime();
    playerRef.current.seekTo(Math.min(currentTime + 15, duration), 'seconds');
  };
  

const toggleMute = () => {
  setMuted((prev) => !prev);
};
  
useEffect(() => {
  setMuted(volume === 0);
}, [volume]);


  React.useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      if (wrapperRef.current.requestFullscreen) {
        wrapperRef.current.requestFullscreen();
      } else if (wrapperRef.current.mozRequestFullScreen) { /* Firefox */
        wrapperRef.current.mozRequestFullScreen();
      } else if (wrapperRef.current.webkitRequestFullscreen) { /* Chrome, Safari & Opera */
        wrapperRef.current.webkitRequestFullscreen();
      } else if (wrapperRef.current.msRequestFullscreen) { /* IE/Edge */
        wrapperRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) { /* Firefox */
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) { /* Chrome, Safari & Opera */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) { /* IE/Edge */
        document.msExitFullscreen();
      }
    }
  };
  
  


  
  useEffect(() => {
    if (isPlaying && playerRef.current && played) {
      playerRef.current.seekTo(played, 'fraction');
    }
  }, [isPlaying]);


useEffect(() => {
  window.addEventListener('beforeunload', saveResumeData);
  return () => {
    window.removeEventListener('beforeunload', saveResumeData);
  };
}, []);

const handleProgress = (state) => {
  setPlayed(state.played);
  lastPlayedTimeRef.current = state.played;
  currentTimeRef.current = state.playedSeconds;

  const now = Date.now();
  if (now - lastSaveRef.current > 3000) {
    lastSaveRef.current = now;
    saveResumeData();
  }
};
  
  const handlePlay = () => {
    if (!hasStarted) {
      const el = wrapperRef.current;
      if (el?.requestFullscreen) el.requestFullscreen();
      else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else if (el?.msRequestFullscreen) el.msRequestFullscreen();
  
      setHasStarted(true);
    }
    setIsPlaying(true);
  };

  const togglePlayPause = () => {
    setIsPlaying((prev) => !prev);
    resetInactivityTimer();
  };

    
const saveResumeData = () => {
  if (movieDataRef.current?.fullmovie && currentTimeRef.current > 0 && duration > 0) {
    localStorage.setItem(`resume:${movieParam}`, JSON.stringify({
      time: currentTimeRef.current,
      duration: duration,
      url: movieDataRef.current.fullmovie,
      savedAt: Date.now(),
    }));
  }
};
  
useEffect(() => {
  const saved = localStorage.getItem(`resume:${movieParam}`);
  if (saved) {
    try {
      const { time, duration: savedDuration } = JSON.parse(saved);
      if (time && savedDuration) {
        setDuration(savedDuration);
        setPlayed(time / savedDuration);
      }
    } catch (e) {
      console.error('Failed to parse resume data:', e);
    }
  }
}, [movieParam]);
  
  const handleExit = () => {
    const internalPlayer = playerRef.current?.getInternalPlayer();

    if (document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement) {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  }
    setShowVideoPlayer(false);
  
    if (internalPlayer && typeof internalPlayer.pause === 'function') {
      setSavedTime(playerRef.current.getCurrentTime());
  
      internalPlayer.pause();
    }
    saveResumeData();
  
    setHasStarted(false);
    setIsPlaying(false);
  };

useEffect(() => {
  return () => {
    setShowVideoPlayer(false);
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    saveResumeData();
  };
}, []);
  
  useEffect(() => {
    function handleClickOutside(event) {
      if (sliderRef.current && !sliderRef.current.contains(event.target)) {
        setShowSlider(false);
      }
    }
    if (showSlider) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSlider]);


  useEffect(() => {
    const fetchMovieData = async () => {
      try {
        const responses = await Promise.all([
          fetch(`https://second-team-productions.onrender.com/movies/${movieParam}`),
          fetch(`https://second-team-productions.onrender.com/newmovies/${movieParam}`),
          fetch(`https://second-team-productions.onrender.com/hitchcockmovies/${movieParam}`)
        ]);
  
        // Check if both responses are ok
        responses.forEach(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch data');
          }
        });
  
        const data = await Promise.all(responses.map(response => response.json()));
  
        // Combine the results or choose one based on preference or availability
        let combinedData = [...data[0], ...data[1], ...data[2]];
  
        // Choose the first valid data set to use
        const validData = combinedData.length > 0 ? combinedData[0] : null;
  
        if (validData) {
          setMovieData(validData);
          setIsLoaded(true);
        } else {
          throw new Error('No valid movie data found');
        }
  
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message);
      }
    };
  
    fetchMovieData();
  }, [movieParam]);

useEffect(() => {
  if (isLoaded) {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    const timeout = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 100);

    return () => clearTimeout(timeout);
  }
}, [isLoaded]);

 if (!isLoaded) {
  return <Spinner fullPage label="Loading" />;
}
 
 
  return (
    <div>
      {error ? (
        <div>Error: {error.message}</div>
      ) : (
      <>
        <div 
        style={{ 
          justifyContent: 'center', 
          alignItems: 'center',
          
          // marginTop: '10%',
          // marginBottom: '5%',
          }}>
          {!hasStarted ? (  
         <div 
         style={{
          marginRight: '3.5%',
          marginLeft: '3.5%',
          
          }}
          >
      {/* <HeroBackground image="allbg.jpg"  bgColor="#131212" /> */}
        <div 
        style={{
          position: 'relative',
          zIndex: 0,
          width: '100%',
          height: '100%',
          minHeight: '420px',
          maxHeight: '520px',
          overflow: 'hidden',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          borderRadius: "30px",
          border: '2px solid rgba(28, 26, 26, 0.9)',
          boxShadow: `
            0 2px 0 rgba(255, 255, 255, 0.03) inset,
            0 -2px 0 rgba(0, 0, 0, 0.5) inset,
            0 12px 24px rgba(0, 0, 0, 0.6),
            0 32px 64px rgba(0, 0, 0, 0.5),
            0 64px 100px rgba(0, 0, 0, 0.35)
          `,
                  ...(window.innerWidth <= 768 ? { maxHeight: '65vh', minHeight: 'auto' } : {}),
        }}>

          
            <div>   
                {movieData?.mainimage && (
                  <img
                    src={movieData.mainimage}
                    alt="main image"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain', 
                      position: 'relative',
                      zIndex: 0,
                    }}
                    onClick={handlePlay}
                  />
                )}
              </div>
              <div  className='col-6 card-overlay'
               style={{ 
                position: 'absolute', 
                top: '20%', 
                marginLeft: '1%',
                zIndex: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.5)', 
                borderRadius: '8px', // 
                padding: '1rem',    
                maskImage: `
                linear-gradient(to bottom, white 0%, white 95%, transparent 100%),
                linear-gradient(to top, white 0%, white 90%, transparent 100%),
                linear-gradient(to right, white 0%, white 95%, transparent 100%)`,
              WebkitMaskImage: `
                linear-gradient(to bottom, white 0%, white 95%, transparent 100%),
                linear-gradient(to top, white 0%, white 90%, transparent 100%),
                linear-gradient(to right, white 0%, white 95%, transparent 100%)`,
              WebkitMaskComposite: 'destination-in',
              maskComposite: 'intersect',
              transition: 'mask-image 0.9s ease',
              }} >
                   <Card> 
                     <h6 className='title-font' 
                     style={{ 
                      color: movieData && movieData.fontColor,
                      textShadow: movieData && movieData.textShadow
                        }}> 
                     {movieData && movieData.moviename}
                     </h6>
                     {/* <div style={{ display: 'flex', gap: '1rem'  }}>  
                       <Card.Text>{movieData && movieData.year}</Card.Text>
                       <Card.Text>{movieData && movieData.runtime}</Card.Text>
                     </div> */}
                     {/* <Card.Text style={{ marginTop: '1%' }}>
                     {movieData && movieData.moviedescription}
                     </Card.Text> */}
                     <div>
                     <Button className="play-details-resume-button" 
                       style={{
                        backgroundColor: 'rgb(67, 66, 66)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.2rem',
                        // padding: '0.5rem 1rem',
                        marginTop: '2%'
                      }}
                       onClick={handlePlay}
                     >
                        {!(hasStarted || currentTime > 0) && (
                        <Play
                          style={{
                            color: '#131212',
                            fill: '#131212',
                            fontSize: 'clamp(1.3rem, 3vw, 1.5rem)',
                            flex: 1,
                            textAlign: 'right',
                          }}
                        />
                      )}              
                      <div >
                        <h5 className="play-details-resume-button-text"
                          style={{
                            margin: 0,
                            color: 'white',
                            fontSize: 'inherit',
                            textAlign: 'left'
                          }}
                        >
                          {hasStarted || currentTime > 0 ? 'Resume' : 'Play'}
                        </h5>

                        {(hasStarted || currentTime > 0) && (
                          <div >
                            <div
                              style={{
                                height: '6px',
                                background: '#555',
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}
                            >
                              <div
                                style={{
                                  width: `${played * 100}%`,
                                  background: 'rgb(116, 12, 12)',
                                  height: '100%'
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                     </Button>
                     </div>
                   </Card>
                 </div>  
                </div>  
        <Card 
        className="card-body"
          style={{
            maxWidth: '1100px',
            width: '100%',
            marginTop: '5%',
            marginLeft: 'auto',
            marginRight: 'auto',
            padding: '1%',
            justifyContent: 'center',
            
            }}
            > 


<div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center'
style={{ 
  gap: 'clamp(0.05rem, .05vw, .05rem)',
  justifyContent: 'center',
  }}>

          {/* LEFT COLUMN */}
         
            <div 
            style={{ 
              flex: 1.3 ,
              display: 'flex', 
              alignContent: 'center', 
              flexWrap: 'wrap',
              padding: '1%',
              }}>
                 <h7
              style={{
                  color: movieData && movieData.fontColor,
                  textShadow: movieData && movieData.textShadow
              }}
              >Plot
              </h7>
                
            <Card.Text style={{ textAlign: 'left', width: '100%' }}>
              {movieData && movieData.moviedescription}
            </Card.Text>
            <div
            style={{
              display: 'block',
              width: '100%',
            }}
            >
              <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'left',
                gap: '.5rem',
              }}
              >
            <h7
              style={{
                  color: movieData && movieData.fontColor,
                  textShadow: movieData && movieData.textShadow
              }}
              >Release Year :
              </h7>
            <Card.Text>
            {movieData && movieData.year}
            </Card.Text>
            </div>
              <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'left',
                gap: '.5rem',
              }}
              >
                    <h7
                style={{
                color: movieData && movieData.fontColor,
                textShadow: movieData && movieData.textShadow
                }}
                >Runtime : </h7>
              <Card.Text>
                {movieData && movieData.runtime}
              </Card.Text>
              </div>
            </div>
            </div>
         

          {/* RIGHT COLUMN */}
          <div 
          style={{ 
            flex: 1,
            padding: '1%',
            }}>
             <h7
                style={{
                    color: movieData && movieData.fontColor,
                      textShadow: movieData && movieData.textShadow
                }}
                >Director</h7>
             <div>
              <CardText>
                <div className='row g-3 justify-content-center'>
                  <div className='col-12 col-md-6 d-flex flex-column align-items-center text-center'
                    style={{ position: 'relative', marginBottom: '.25rem' }}
                  >
                    <div>
                      {movieData && movieData.directorimage && (
                        <img
                          src={movieData.directorimage}
                          alt="Director Image"
                       style={{
                        borderRadius: "50%",
                        width: 'clamp(40px, 12vw, 80px)',
                        height: 'clamp(40px, 12vw, 80px)',
                        objectFit: 'cover',
                        marginBottom: '0.25rem',
                      }}
                        />
                      )}
                    </div>
                    {movieData && movieData.director}
                  </div>

                
                 
                </div>
              </CardText>
            </div>

            <h7
                style={{
                    color: movieData && movieData.fontColor,
                      textShadow: movieData && movieData.textShadow
                }}
                >Cast</h7>

            <div>
              <CardText>
                <div className='row g-3'>
                  <div className='col-6 d-flex flex-column align-items-center text-center'
                    style={{ position: 'relative' }}
                  >
                    <div>
                      {movieData && movieData.cast1image && (
                        <img
                          src={movieData.cast1image}
                          alt="Cast1 Image"
                         style={{
                          borderRadius: "50%",
                          width: 'clamp(40px, 12vw, 80px)',
                          height: 'clamp(40px, 12vw, 80px)',
                          objectFit: 'cover',
                          marginBottom: '0.25rem',
                        }}
                        />
                      )}
                    </div>
                    {movieData && movieData.cast1}
                  </div>

                  <div className='col-6 d-flex flex-column align-items-center text-center'
                    style={{ position: 'relative' }}
                  >
                    <div>
                      {movieData && movieData.cast2image && (
                        <img
                          src={movieData.cast2image}
                          alt="Cast2 Image"
                       style={{
                        borderRadius: "50%",
                        width: 'clamp(40px, 12vw, 80px)',
                        height: 'clamp(40px, 12vw, 80px)',
                        objectFit: 'cover',
                        marginBottom: '0.25rem',
                      }}
                        />
                      )}
                    </div>
                    {movieData && movieData.cast2}
                  </div>

                  {/* <div className='col-12 col-md-4 d-flex flex-column align-items-center text-center'
                    style={{ position: 'relative' }}
                  >
                    <div>
                      {movieData && movieData.cast3image && (
                        <img
                          src={movieData.cast3image}
                          alt="Cast3 Image"
                          style={{
                            borderRadius: "50%",
                            width: '120px',
                            height: '120px',
                            objectFit: 'cover',
                            marginBottom: '0.25rem',
                          }}
                        />
                      )}
                    </div>
                    {movieData && movieData.cast3}
                  </div> */}
                </div>
              </CardText>
            </div>
          </div>

        </div>
      </Card>
          
            <div>
          </div>  
          <div>
          </div>
            </div>
            

          ) : (       
<div className="netflix-player-wrapper viewport-player" ref={wrapperRef}
   style={{
     display: 'flex',
     justifyContent: 'center',
     alignItems: 'center',
     backgroundColor: 'black',
   }}
  >
     {showControls && (
        <Button
          className="video-hover-button"
          onClick={handleExit}
          style={{
            position: 'absolute',
            top: '10%',
            left: '10px',
            backgroundColor: 'transparent',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            cursor: 'pointer',
            fontSize: '16px',
            opacity: 0,
            transition: 'opacity 0.7s ease',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',   
            gap: '10px'            
          }}
        >
          <ChevronLeft size={44} color="silver" />
          <h5 style={{ margin: 0, color: 'white' }}>
            {movieData && movieData.moviename}
          </h5>
        </Button>
        )}

        <ReactPlayer
        ref={playerRef}
        url={movieData.fullmovie}
        playing={isPlaying}
        controls={isMobile} 
        width="100%"
        height="100%"
        volume={volume}
        muted={muted}
        onProgress={handleProgress}
    onReady={() => {
  setShowVideoPlayer(true);
  if (!hasResumedRef.current) {
    hasResumedRef.current = true;
    const saved = localStorage.getItem(`resume:${movieParam}`);
    if (saved) {
      try {
        const { time } = JSON.parse(saved);
        if (time && playerRef.current) {
          playerRef.current.seekTo(time, 'seconds');
        }
      } catch (e) {
        console.error('Failed to parse resume data:', e);
      }
    }
  }
}}
        onDuration={handleDuration}
        config={{
          file: {
            attributes: {
              playsInline: true,
              disablePictureInPicture: true,
              controls: isMobile,
            }
          }
        }}
      />

{!isMobile && showControls && (
      <div className="netflix-controls"
      style={{
        bottom: '0px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
        padding: '10px 20px',
        width: '100%',
        maxWidth: '100vw',
        zIndex: 10,
      }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '5%',
            position: 'absolute',
            bottom: '90%',
            width: '100%',
            zIndex: 2,
          }}
         >
          <Button
            onClick={handleRewind15}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              position: 'relative',
              width: '48px',
              height: '48px',
              padding: 0,
              margin: '2%'
            }}
          >
            <RotateCcw
              size={48}
              strokeWidth={2.5}
              style={{
                color: 'silver',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
            <span
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '14px',
                color: 'white',
                pointerEvents: 'none',
              }}
            >
              15
            </span>
          </Button>

          <Button
            className="center-play-button"
            onClick={togglePlayPause}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: 'none',
              padding: '20px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '100px',
              zIndex: 2,
            }}
          >
            {isPlaying ? (
              <Pause
                fill="currentColor"
                size={64}
                strokeWidth={2.5}
                style={{
                  color: 'silver',
                }}
              />
            ) : (
              <Play
                fill="currentColor"
                size={64}
                strokeWidth={2.5}
                style={{
                  color: 'silver',
                }}
              />
            )}
          </Button>

          <Button
            onClick={handleForward15}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              position: 'relative',
              width: '48px',
              height: '48px',
              padding: 0,
              margin: '2%'
            }}
          >
            <RotateCw
              size={48}
              strokeWidth={2.5}
              style={{
                color: 'silver',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
            <span
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '14px',
                color: 'white',
                pointerEvents: 'none',
              }}
            >
              15
            </span>
          </Button>
        </div>



      
    <div className="sliders-container"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        padding: '0 20px'
      }}>
      <input
        ref={sliderRef}
        className="player-slider"
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={played}
        onChange={(e) => playerRef.current.seekTo(parseFloat(e.target.value))}
        onMouseMove={(e) => {
          if (!sliderRef.current) return;
          const rect = sliderRef.current.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const ratio = x / rect.width;
          const time = ratio * totalTime;
          const timeRemaining = totalTime - (ratio * totalTime);
          setHoverTime(timeRemaining);
          setHoverTime(time);
          setHoverX(x);
        }}
        onMouseLeave={() => {
          setHoverTime(null);
          setHoverX(null);
        }}
        style={{ '--played': played }}
      />
      {hoverTime !== null && (
          <div
            className="slider-tooltip"
            style={{
              left: `${hoverX}px`
            }}
          >
            {formatTime(hoverTime)}
          </div>
        )}
        <div className="time-display">
        <CardText>
          {formatTime(totalTime - currentTime)}
        </CardText>
        </div>
        
        <div
        style={{ position: 'relative', cursor: 'pointer', display: 'inline-block' }}
        onMouseEnter={() => {
          clearTimeout(hideTimeoutRef.current);
          setShowVolumeSlider(true);
        }}
        onMouseLeave={() => {
          hideTimeoutRef.current = setTimeout(() => {
            setShowVolumeSlider(false);
          }, 300);
        }}
      >
        {muted ? (
          <VolumeX 
            color="silver" 
            size={24}
            onClick={toggleMute}
          />
        ) : (
          <Volume2 
            color="silver" 
            size={24}
            onClick={toggleMute}
          />
        )}

        {showVolumeSlider && (
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={(e) => {
              const newVolume = parseFloat(e.target.value);
              setVolume(newVolume);
              if (newVolume === 0) {
                setMuted(true);
              } else {
                setMuted(false);
              }
            }}
            className="custom-slider volume-slider"
            style={{
              position: 'absolute',
              bottom: '80px',
              left: '50%',
              transform: 'translateX(-50%) rotate(-90deg)',
              width: '100px',
              height: '8px',
              zIndex: 10,
              '--progress': `${volume * 100}%`
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>

      <div
        style={{ position: 'relative', cursor: 'pointer', display: 'inline-block', marginLeft: '1%' }}
        onClick={handleToggleFullscreen}
        title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
      >
        {isFullscreen ? (
          <Minimize color="silver" size={24} />
        ) : (
          <Maximize color="silver" size={24} />
        )}
      </div>

        </div>
        
      </div>
      )}
    </div>    
          )}
          </div>
        </>
      )}
    </div>
  );
}

export default MovieProfile;